/**
 * "Has this visitor unlocked the demo?" (SKE-4166, phase 3).
 *
 * Kept in its own localStorage entry rather than inside the demo store, for one
 * behavioural reason: **"Reset demo" resets the board, not the person**. A
 * visitor who has already given their email and then resets the posts must not
 * be asked for it again — re-prompting a known lead is annoying, and it would
 * double-count the email-unlock KPI.
 *
 * Same `useSyncExternalStore` contract as `demoStore` (subscribe + a snapshot
 * whose identity only changes when the value changes) so React components can
 * bind to it without polling.
 */

export const DEMO_UNLOCK_KEY = 'sked.demo.approvals.unlock.v1'

export interface DemoUnlockState {
  unlockedAt: string
  /**
   * Email domain only — never the address. The unlocked UI wants to say
   * something specific ("we'll email your @acme.com team"), and phase 4 keys
   * personalization off the domain; storing the full address in localStorage
   * buys nothing and is PII sitting in a browser we don't control.
   */
  emailDomain?: string
}

type Listener = () => void

const listeners = new Set<Listener>()

export const subscribeDemoUnlock = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const notify = (): void => {
  for (const listener of [...listeners]) {
    listener()
  }
}

// `undefined` = not yet read this page load; `null` = read, not unlocked.
let cached: DemoUnlockState | null | undefined

const storage = (): Storage | null => {
  try {
    return globalThis.window?.localStorage ?? null
  } catch {
    return null
  }
}

/** `getSnapshot` — stable identity between writes, as React requires. */
export const getDemoUnlockSnapshot = (): DemoUnlockState | null => {
  if (cached !== undefined) {
    return cached
  }

  try {
    const raw = storage()?.getItem(DEMO_UNLOCK_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    cached =
      parsed && typeof (parsed as DemoUnlockState).unlockedAt === 'string'
        ? (parsed as DemoUnlockState)
        : null
  } catch {
    cached = null
  }

  return cached
}

export const isDemoUnlocked = (): boolean => getDemoUnlockSnapshot() !== null

/**
 * The full address, held in memory for THIS page load only.
 *
 * Phase 6's "email me a summary" wants to send without asking a visitor who
 * already gave us their address thirty seconds ago — but the reason the stored
 * state keeps only the domain still stands, so this is deliberately not
 * persisted anywhere. A visitor returning on a later page load is asked again
 * (one prefilled-vs-empty field, never a dead end), which is a small cost for
 * keeping an address out of a browser store we don't control.
 */
let capturedEmail: string | null = null

/** The address given this page load, if there was one. */
export const getCapturedDemoEmail = (): string | null => capturedEmail

export const markDemoUnlocked = (email: string): DemoUnlockState => {
  const state: DemoUnlockState = {
    unlockedAt: new Date().toISOString(),
    emailDomain: email.split('@')[1]?.trim().toLowerCase() || undefined,
  }

  capturedEmail = email.trim().toLowerCase() || null
  cached = state
  try {
    storage()?.setItem(DEMO_UNLOCK_KEY, JSON.stringify(state))
  } catch {
    // Quota / privacy error — the in-memory copy still serves this page load.
  }
  notify()

  return state
}

/** QA + tests: forget the unlock so the gate can be exercised again. */
export const clearDemoUnlock = (): void => {
  capturedEmail = null
  cached = null
  try {
    storage()?.removeItem(DEMO_UNLOCK_KEY)
  } catch {
    // Nothing useful to do.
  }
  notify()
}
